import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLogin } from "./hooks/useLogin";
import { TextInput } from "@/components/form/TextInput";
import { PhoneInput } from "@/components/form/PhoneInput";
import { validateLoginForm } from "./validation";
import { InputLoader } from "@/components/ui/InputLoader";
import { assets } from "@/utils/assets";
import { useAuthStore } from "./store";
import { useTranslation } from "react-i18next";
import { paths } from "@/navigation/paths";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";

export const LoginPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const successMessage = location.state?.message || null;
  const wallpapers = assets.loginWallpapers;
  const [wallpaperIndex, setWallpaperIndex] = React.useState(0);
  const currentWallpaper = wallpapers[wallpaperIndex % wallpapers.length];

  React.useEffect(() => {
    if (wallpapers.length <= 1) return;
    const intervalId = setInterval(() => {
      setWallpaperIndex((prev) => (prev + 1) % wallpapers.length);
    }, 8000);
    return () => clearInterval(intervalId);
  }, [wallpapers.length]);
  const {
    formData,
    fieldsErrors,
    onSubmit,
    onChangeField,
    onChangePhoneField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
  } = useLogin();

  const isLoading = useAuthStore((s) => s.isLoading);
  const errorMessage = useAuthStore((s) => s.errorMessage);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearFieldErrorsHandler();
    const errors = validateLoginForm(formData);

    if (Object.keys(errors).length > 0) {
      setFieldErrorsHandler(errors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="flex min-h-screen flex-col lg:h-full lg:flex-row">
      <div className="flex w-full flex-1 flex-col justify-center items-center bg-white px-6 py-10 sm:px-10 lg:w-1/2 lg:px-8 lg:py-0 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <div className="mx-auto w-full max-w-md lg:w-1/2">
          <img src={assets.logo} alt="logo" className="mx-auto mb-8 h-20 lg:h-18 lg:mb-19" />

          <form onSubmit={handleSubmit} className="space-y-6">
            <h1 className="mb-6 text-3xl font-bold text-gray-900">{t("login.title")}</h1>
            <PhoneInput
              label={t("login.phoneLabel")}
              name="phone"
              id="phone"
              placeholder={t("login.phonePlaceholder")}
              value={formData.phone}
              onChange={(value) => onChangePhoneField(value)}
              error={fieldsErrors.phone}
            />

            <TextInput
              label={t("login.passwordLabel")}
              name="password"
              id="password"
              type="password"
              placeholder={t("login.passwordPlaceholder")}
              value={formData.password}
              onChange={(e) => onChangeField(e, "password")}
              error={fieldsErrors.password}
            />

            <div className="text-center">
              <Link
                to={paths.FORGOT_PASSWORD}
                className="text-sm font-semibold text-primary hover:text-primary-700"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>

            {successMessage && (
              <div className="text-green-500 text-sm">{successMessage}</div>
            )}
            {errorMessage && (
              <div className="text-red-500 text-sm">{errorMessage}</div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="mt-8 w-full rounded-full bg-primary px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-300 lg:mt-10 lg:w-60 lg:h-15 lg:rounded-4xl lg:block lg:mx-auto"
            >
              {isLoading ? <InputLoader /> : t("login.signIn")}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t("login.noAccount")}{" "}
              <Link
                to={paths.SIGN_UP}
                className="font-semibold text-primary hover:text-primary-700"
              >
                {t("login.signUp")}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden items-center justify-center lg:flex lg:w-1/2">
        <div className="relative h-full w-full">
          <img
            key={currentWallpaper}
            src={currentWallpaper}
            alt="login wallpaper"
            className="h-full w-full object-cover transition-opacity duration-700 ease-in-out"
          />
        </div>
      </div>
    </div>
  );
};
